import AccountCircle from '@mui/icons-material/AccountCircle';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, Menu, MenuItem, Toolbar, Tooltip } from '@mui/material';
import { grey as gray } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { signOut } from 'aws-amplify/auth';
import * as React from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';

import useMixPanel from '@/hooks/useMixPanel';

import { useAdminAccess } from '../../useAdminAccess';
import { ErrorBoundary } from '../ErrorBoundary';
import { PaymentWarningBanner } from '../PaymentWarningBanner';
import { WaterDroplets } from '../WaterDroplets';
import { MiniAppBar } from './components';
import { Drawer } from './Drawer';
import { drawerWidth } from './util';

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%',
  },
  appBar: {
    // width: `calc(100% - ${drawerWidth})`,
    // marginLeft: drawerWidth
    backgroundColor: 'white',
    '@media print': {
      display: 'none',
    },
  },
  color: {
    color: gray[50],
  },
  content: {
    backgroundColor: 'white',
    boxSizing: 'border-box',
    width: `calc(100% - ${drawerWidth})`,
    marginLeft: drawerWidth,
    height: 'calc(100vh - 64px)',
    marginTop: 64,
    padding: 24,
    overflowY: 'scroll',
    '@media print': {
      height: 'auto',
      marginTop: 0,
      marginLeft: 0,
      width: '100%',
      overflow: 'visible',
      padding: 12,
    },
  },
  fullContent: {
    backgroundColor: 'white',
    boxSizing: 'border-box',
    height: 'calc(100vh - 64px)',
    marginTop: 64,
    padding: 24,
    overflowY: 'scroll',
    width: `calc(100% - ${'64px'})`,
    marginLeft: '64px',
    '@media print': {
      height: 'auto',
      marginTop: 0,
      marginLeft: 0,
      width: '100%',
      overflow: 'visible',
      padding: 12,
    },
  },
  headerText: {
    color: '#353838',
    fontWeight: 'bold',
    fontFamily: 'BC Alphapipe L',
  },
  headerLogo: {
    height: 55,
    cursor: 'pointer',
  },
});

interface AppShellProps {
  hideDrawer?: boolean;
}

export const AppShell = ({ hideDrawer = false }: AppShellProps): JSX.Element => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [openDrawer, setOpenDrawer] = React.useState(true);
  const [logoClickCount, setLogoClickCount] = React.useState(0);
  const [showWaterDroplets, setShowWaterDroplets] = React.useState(false);
  const [waterDropletTriggerKey, setWaterDropletTriggerKey] = React.useState(0);
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { deleteAdminInfo } = useAdminAccess();
  const { trackEvent } = useMixPanel();

  /**
   * Handle menu open
   *
   * @param  {MouseEvent} event - User event that triggered the open
   */
  const handleMenu = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  /**
   * Logout
   */
  const logout = async () => {
    handleClose();
    try {
      await trackEvent('User Log Out');
      deleteAdminInfo();
      await signOut();
    } catch (e) {
      console.error(e);
    } finally {
      window.location.assign('/');
    }
  };

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  /**
   * Handle logo click for water droplet effect easter egg
   */
  const handleLogoClick = (event: React.MouseEvent) => {
    event.preventDefault();

    // Don't allow triggering while animation is already running
    if (showWaterDroplets) {
      // Still navigate to home
      setTimeout(() => {
        navigate('/');
      }, 10);
      return;
    }

    // Clear existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);

    if (newCount === 5) {
      // Trigger water droplets effect
      setWaterDropletTriggerKey((prev) => prev + 1);
      setShowWaterDroplets(true);
      setLogoClickCount(0);
    } else {
      // Reset count after 1 second of no clicks
      clickTimeoutRef.current = setTimeout(() => {
        setLogoClickCount(0);
      }, 1000);
    }

    // Navigate to home after a short delay to allow click counting
    setTimeout(() => {
      navigate('/');
    }, 10);
  };

  /**
   * Handle water droplets animation complete
   */
  const handleWaterDropletsComplete = () => {
    setShowWaterDroplets(false);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <div data-component-id={'APP-SHELL'} className={classes.root}>
        <PaymentWarningBanner />
        <MiniAppBar position="fixed" className={classes.appBar} open={!hideDrawer && openDrawer}>
          <Toolbar>
            {!hideDrawer && (
              <IconButton
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  marginRight: 5,
                  ...(openDrawer && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/" onClick={handleLogoClick}>
              <img src="/logo-light.png" alt="SimpleSub Water" className={classes.headerLogo} />
            </Link>
            <div style={{ marginLeft: 'auto' }}>
              <Tooltip title="Contact Support" placement="bottom" arrow>
                <a
                  href="https://simplesubwatersupport.zendesk.com/hc/en-us/requests/new"
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconButton
                    aria-label="contact support"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    size="large"
                    color="primary"
                  >
                    <ContactSupportIcon />
                  </IconButton>
                </a>
              </Tooltip>
              <IconButton
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                size="large"
                color="primary"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => {
                    navigate('/account/settings');
                    handleClose();
                  }}
                >
                  Settings
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate('/accounts/reset-password');
                    handleClose();
                  }}
                >
                  Reset Password
                </MenuItem>
                <MenuItem onClick={logout}>Logout</MenuItem>
              </Menu>
            </div>
          </Toolbar>
        </MiniAppBar>
        {!hideDrawer && (
          <Box sx={{ '@media print': { display: 'none' } }}>
            <Drawer open={openDrawer} setOpen={setOpenDrawer} />
          </Box>
        )}
        <div
          data-component-id={'APP-SHELL_CHILDREN-CONTAINER'}
          className={!hideDrawer && openDrawer ? classes.content : classes.fullContent}
        >
          <Outlet />
        </div>
        {showWaterDroplets && (
          <WaterDroplets
            onComplete={handleWaterDropletsComplete}
            triggerKey={waterDropletTriggerKey}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};
